import React, { useState, useEffect } from 'react';
import { Car, Fuel, Zap, Battery, Calculator, Info, RefreshCw, FileText, Handshake } from 'lucide-react';

const ENGINE_TYPES = [
  { id: 'petrol_small', label: 'Benzyna/Diesel', sublabel: 'do 2000cm³', rate: 0.031, icon: Fuel },
  { id: 'petrol_large', label: 'Benzyna/Diesel', sublabel: 'powyżej 2000cm³', rate: 0.186, icon: Fuel },
  { id: 'hybrid_small', label: 'Hybryda', sublabel: 'do 2000cm³', rate: 0.0155, icon: Battery },
  { id: 'hybrid_large', label: 'Hybryda', sublabel: 'powyżej 2000cm³', rate: 0.093, icon: Battery },
  { id: 'plugin_small', label: 'Plug-in Hybrid', sublabel: 'do 2000cm³', rate: 0, icon: Zap },
  { id: 'plugin_large', label: 'Plug-in Hybrid', sublabel: 'powyżej 2000cm³', rate: 0.093, icon: Zap },
  { id: 'electric', label: 'Elektryczny', sublabel: '100% EV', rate: 0, icon: Zap },
];

const FIXED_COSTS = {
  commission: 2460,
  transport: 2800,
  registration: 400,
  inspection: 750,
  vinCheck: 130,
};

const GERMAN_VAT_RATE = 0.19;
const POLISH_VAT_RATE = 0.23;
const SPREAD = 0.01; // 1 grosz spreadu

const formatPLN = (value) => {
  return new Intl.NumberFormat('pl-PL', {
    style: 'currency',
    currency: 'PLN',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
};

const formatEUR = (value) => {
  return new Intl.NumberFormat('pl-PL', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
};

export default function ImportCalculator() {
  const [carPriceEur, setCarPriceEur] = useState('');
  const [selectedEngine, setSelectedEngine] = useState(null);
  const [nbpRate, setNbpRate] = useState(null);
  const [exchangeRate, setExchangeRate] = useState(null);
  const [isLoadingRate, setIsLoadingRate] = useState(true);
  const [purchaseType, setPurchaseType] = useState('private'); // 'private' or 'vat_invoice'

  // Fetch NBP rate and set exchange rate with spread
  const fetchNbpRate = async () => {
    setIsLoadingRate(true);
    try {
      const response = await fetch('https://api.nbp.pl/api/exchangerates/rates/a/eur/?format=json');
      const data = await response.json();
      if (data.rates && data.rates[0]) {
        const nbp = data.rates[0].mid;
        setNbpRate(nbp);
        setExchangeRate(Math.round((nbp + SPREAD) * 10000) / 10000); // NBP + 1gr spread
      }
    } catch (error) {
      console.error('Failed to fetch NBP rate:', error);
      // Fallback rate if API fails
      setNbpRate(4.20);
      setExchangeRate(4.21);
    } finally {
      setIsLoadingRate(false);
    }
  };

  useEffect(() => {
    fetchNbpRate();
  }, []);

  // Calculate prices based on purchase type
  const carPriceEurNum = carPriceEur ? parseFloat(carPriceEur) : 0;
  
  // For VAT invoice: price in EUR is GROSS (with 19% German VAT), we need NET
  const carPriceEurNet = purchaseType === 'vat_invoice' 
    ? carPriceEurNum / (1 + GERMAN_VAT_RATE) 
    : carPriceEurNum;
  
  // Convert to PLN
  const carPricePlnNet = carPriceEurNet * exchangeRate;
  
  // Excise tax is calculated on NET price in PLN
  const exciseTax = selectedEngine ? carPricePlnNet * selectedEngine.rate : 0;
  
  // For VAT invoice: add Polish VAT on (NET + excise)
  const polishVat = purchaseType === 'vat_invoice' 
    ? (carPricePlnNet + exciseTax) * POLISH_VAT_RATE 
    : 0;
  
  // Car price to display (what customer pays for the car)
  const carPriceFinal = purchaseType === 'vat_invoice'
    ? carPricePlnNet + exciseTax + polishVat
    : carPricePlnNet;
  
  const totalFixedCosts = Object.values(FIXED_COSTS).reduce((a, b) => a + b, 0);
  const totalPrice = carPriceFinal + (purchaseType === 'private' ? exciseTax : 0) + totalFixedCosts;

  const canCalculate = carPriceEur && selectedEngine;

  return (
    <div className="min-h-screen bg-[#050505] text-white">
      {/* Background gradient */}
      <div 
        className="fixed inset-0 opacity-30 pointer-events-none"
        style={{
          background: 'radial-gradient(ellipse at 50% 0%, rgba(249,115,22,0.15) 0%, transparent 60%)',
        }}
      />
      
      <main className="relative z-10 max-w-2xl mx-auto px-4 py-8 md:py-16">
        {/* Header */}
        <header className="text-center mb-10">
          <div className="relative inline-block mb-2 overflow-visible">
            {/* Outer glow */}
            <div 
              className="absolute inset-0 blur-3xl opacity-50"
              style={{
                background: 'radial-gradient(circle, rgba(249, 115, 22, 0.6) 0%, transparent 70%)',
                transform: 'scale(1.5)',
              }}
            />
            {/* Logo with CSS mask for soft edges */}
            <div 
              className="relative"
              style={{
                maskImage: 'radial-gradient(ellipse 80% 75% at center, black 50%, transparent 80%)',
                WebkitMaskImage: 'radial-gradient(ellipse 80% 75% at center, black 50%, transparent 80%)',
              }}
            >
              <img 
                src="https://customer-assets.emergentagent.com/job_whatsapp-car-agent/artifacts/sj9m1ro3_Screenshot_20260215_005014_Gallery.jpg" 
                alt="Hellboy's Garage" 
                className="h-44 md:h-56 object-contain"
                data-testid="logo"
              />
            </div>
          </div>
          <h1 className="font-heading text-3xl md:text-4xl font-black uppercase tracking-tight text-white">
            Kalkulator Importu
          </h1>
          <p className="text-zinc-400 text-sm max-w-md mx-auto mt-2">
            Oblicz całkowity koszt sprowadzenia samochodu z Niemiec
          </p>
        </header>

        {/* Main Calculator Card */}
        <div className="bg-glass rounded-3xl p-6 md:p-8 shadow-2xl space-y-8">
          
          {/* Purchase Type Selector */}
          <section>
            <label className="text-xs font-bold uppercase tracking-widest text-orange-500 block mb-4">
              Rodzaj zakupu
            </label>
            
            <div className="grid grid-cols-2 gap-3">
              <button
                onClick={() => setPurchaseType('private')}
                data-testid="purchase-private"
                data-state={purchaseType === 'private' ? 'active' : 'inactive'}
                className={`
                  flex flex-col items-center justify-center p-4 rounded-xl border transition-all duration-200
                  ${purchaseType === 'private'
                    ? 'bg-orange-500 border-orange-500 text-white shadow-glow' 
                    : 'bg-white/5 border-white/10 hover:bg-white/10 hover:border-orange-500/50'
                  }
                `}
              >
                <Handshake className={`w-6 h-6 ${purchaseType === 'private' ? 'text-white' : 'text-orange-500'}`} />
                <span className={`text-sm font-bold uppercase mt-2 ${purchaseType === 'private' ? 'text-white' : 'text-zinc-400'}`}>
                  Umowa / VAT marża
                </span>
                <span className={`text-[10px] mt-1 ${purchaseType === 'private' ? 'text-orange-100' : 'text-zinc-500'}`}>
                  Osoba prywatna / dealer
                </span>
              </button>
              
              <button
                onClick={() => setPurchaseType('vat_invoice')}
                data-testid="purchase-vat"
                data-state={purchaseType === 'vat_invoice' ? 'active' : 'inactive'}
                className={`
                  flex flex-col items-center justify-center p-4 rounded-xl border transition-all duration-200
                  ${purchaseType === 'vat_invoice'
                    ? 'bg-orange-500 border-orange-500 text-white shadow-glow' 
                    : 'bg-white/5 border-white/10 hover:bg-white/10 hover:border-orange-500/50'
                  }
                `}
              >
                <FileText className={`w-6 h-6 ${purchaseType === 'vat_invoice' ? 'text-white' : 'text-orange-500'}`} />
                <span className={`text-sm font-bold uppercase mt-2 ${purchaseType === 'vat_invoice' ? 'text-white' : 'text-zinc-400'}`}>
                  Faktura VAT 23%
                </span>
                <span className={`text-[10px] mt-1 ${purchaseType === 'vat_invoice' ? 'text-orange-100' : 'text-zinc-500'}`}>
                  WNT - pełny VAT
                </span>
              </button>
            </div>
            
            {purchaseType === 'vat_invoice' && (
              <div className="mt-3 p-3 bg-orange-500/10 border border-orange-500/20 rounded-lg">
                <p className="text-xs text-orange-200">
                  <span className="font-bold">Tryb WNT:</span> Cena netto DE + akcyza + 23% VAT PL. 
                  Auto będzie ~4% droższe niż przy zakupie na umowę.
                </p>
              </div>
            )}
          </section>

          {/* Price Input Section */}
          <section>
            <div className="flex items-center justify-between mb-4">
              <label className="text-xs font-bold uppercase tracking-widest text-orange-500">
                {purchaseType === 'vat_invoice' ? 'Cena brutto (z VAT DE 19%)' : 'Cena samochodu'}
              </label>
            </div>
            
            <div className="relative group">
              <span className="absolute left-0 top-1/2 -translate-y-1/2 text-3xl md:text-4xl font-black text-zinc-600">€</span>
              <input
                type="number"
                value={carPriceEur}
                onChange={(e) => setCarPriceEur(e.target.value)}
                placeholder="0"
                className="w-full bg-transparent border-b-2 border-white/10 focus:border-orange-500 py-4 pl-10 pr-4 text-4xl md:text-5xl font-black text-white focus:outline-none transition-colors placeholder:text-zinc-800"
                data-testid="price-input"
              />
            </div>
            
            {carPriceEur && purchaseType === 'vat_invoice' && (
              <p className="mt-2 text-zinc-500 text-sm">
                Cena netto (bez VAT DE): <span className="text-zinc-300">{formatEUR(carPriceEurNet)}</span>
              </p>
            )}
          </section>

          {/* Exchange Rate Section */}
          <section className="bg-black/20 rounded-xl p-4 border border-white/5">
            <div className="flex items-center justify-between mb-3">
              <label className="text-xs font-bold uppercase tracking-widest text-zinc-500">
                Kurs wymiany EUR/PLN
              </label>
              <button 
                onClick={fetchNbpRate}
                disabled={isLoadingRate}
                className="flex items-center gap-1 text-xs text-zinc-500 hover:text-orange-500 transition-colors disabled:opacity-50"
                data-testid="refresh-rate-btn"
                title="Odśwież kurs NBP"
              >
                <RefreshCw className={`w-3 h-3 ${isLoadingRate ? 'animate-spin' : ''}`} />
                <span>Odśwież</span>
              </button>
            </div>
            
            <div className="flex items-center gap-4">
              <div className="flex-1">
                <div className="relative">
                  {isLoadingRate ? (
                    <div className="h-8 bg-zinc-800/50 rounded animate-pulse"></div>
                  ) : (
                    <input
                      type="number"
                      step="0.001"
                      value={exchangeRate || ''}
                      onChange={(e) => setExchangeRate(parseFloat(e.target.value) || (nbpRate + SPREAD))}
                      className="w-full bg-transparent border-b border-white/10 focus:border-orange-500 py-2 text-xl font-mono text-white focus:outline-none transition-colors"
                      data-testid="exchange-rate-input"
                    />
                  )}
                </div>
                <p className="text-[10px] text-zinc-600 mt-1">
                  Kurs kantorowy (NBP + 1gr spreadu)
                </p>
              </div>
              
              {nbpRate && (
                <div className="text-right">
                  <p className="text-sm font-mono text-zinc-500">{nbpRate.toFixed(4)}</p>
                  <p className="text-[10px] text-zinc-600">Kurs NBP</p>
                </div>
              )}
            </div>
          </section>

          {/* Engine Type Selector */}
          <section>
            <label className="text-xs font-bold uppercase tracking-widest text-orange-500 block mb-4">
              Typ silnika (stawka akcyzy)
            </label>
            
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {ENGINE_TYPES.map((engine) => {
                const Icon = engine.icon;
                const isSelected = selectedEngine?.id === engine.id;
                
                return (
                  <button
                    key={engine.id}
                    onClick={() => setSelectedEngine(engine)}
                    data-testid={`engine-${engine.id}`}
                    data-state={isSelected ? 'active' : 'inactive'}
                    className={`
                      flex flex-col items-center justify-center p-4 rounded-xl border transition-all duration-200
                      ${isSelected 
                        ? 'bg-orange-500 border-orange-500 text-white shadow-glow' 
                        : 'bg-white/5 border-white/10 hover:bg-white/10 hover:border-orange-500/50'
                      }
                    `}
                  >
                    <Icon className={`w-6 h-6 ${isSelected ? 'text-white' : 'text-orange-500'}`} />
                    <span className={`text-xs font-bold uppercase mt-2 ${isSelected ? 'text-white' : 'text-zinc-400'}`}>
                      {engine.label}
                    </span>
                    <span className={`text-[10px] ${isSelected ? 'text-orange-100' : 'text-zinc-500'}`}>
                      {engine.sublabel}
                    </span>
                    <span className={`text-xs font-mono mt-1 ${isSelected ? 'text-orange-100' : 'text-zinc-600'}`}>
                      {(engine.rate * 100).toFixed(2)}%
                    </span>
                  </button>
                );
              })}
            </div>
          </section>

          {/* Cost Breakdown */}
          {canCalculate && (
            <section className="bg-black/30 rounded-xl p-5 border border-white/5 space-y-3">
              <h3 className="text-xs font-bold uppercase tracking-widest text-zinc-500 mb-4">
                Rozbicie kosztów
              </h3>
              
              <div className="space-y-2">
                {purchaseType === 'vat_invoice' ? (
                  <>
                    <div className="flex justify-between items-center py-2 border-b border-white/5">
                      <span className="text-sm text-zinc-400">Cena netto pojazdu</span>
                      <span className="font-mono text-white">{formatPLN(carPricePlnNet)}</span>
                    </div>
                    
                    <div className="flex justify-between items-center py-2 border-b border-white/5">
                      <span className="text-sm text-zinc-400">
                        Akcyza ({(selectedEngine.rate * 100).toFixed(2)}%)
                      </span>
                      <span className="font-mono text-white">{formatPLN(exciseTax)}</span>
                    </div>
                    
                    <div className="flex justify-between items-center py-2 border-b border-white/5 bg-orange-500/5 -mx-5 px-5">
                      <span className="text-sm text-orange-400 font-medium">
                        VAT 23% (od netto + akcyza)
                      </span>
                      <span className="font-mono text-orange-400">{formatPLN(polishVat)}</span>
                    </div>
                    
                    <div className="flex justify-between items-center py-2 border-b border-white/5">
                      <span className="text-sm text-zinc-300 font-medium">Razem za pojazd</span>
                      <span className="font-mono text-white font-bold">{formatPLN(carPriceFinal)}</span>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="flex justify-between items-center py-2 border-b border-white/5">
                      <span className="text-sm text-zinc-400">Cena pojazdu</span>
                      <span className="font-mono text-white">{formatPLN(carPricePlnNet)}</span>
                    </div>
                    
                    <div className="flex justify-between items-center py-2 border-b border-white/5">
                      <span className="text-sm text-zinc-400">
                        Akcyza ({(selectedEngine.rate * 100).toFixed(2)}%)
                      </span>
                      <span className="font-mono text-white">{formatPLN(exciseTax)}</span>
                    </div>
                  </>
                )}
                
                <div className="pt-2 mt-2 border-t border-white/10">
                  <p className="text-[10px] text-zinc-600 uppercase tracking-wider mb-2">Koszty usługi</p>
                </div>
                
                <div className="flex justify-between items-center py-2 border-b border-white/5">
                  <span className="text-sm text-zinc-400">Prowizja</span>
                  <span className="font-mono text-white">{formatPLN(FIXED_COSTS.commission)}</span>
                </div>
                
                <div className="flex justify-between items-center py-2 border-b border-white/5">
                  <span className="text-sm text-zinc-400">Transport</span>
                  <span className="font-mono text-white">{formatPLN(FIXED_COSTS.transport)}</span>
                </div>
                
                <div className="flex justify-between items-center py-2 border-b border-white/5">
                  <span className="text-sm text-zinc-400">Przygotowanie do rejestracji + opłata produktowa</span>
                  <span className="font-mono text-white">{formatPLN(FIXED_COSTS.registration)}</span>
                </div>
                
                <div className="flex justify-between items-center py-2 border-b border-white/5">
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-zinc-400">Oględziny</span>
                    <div className="group relative">
                      <Info className="w-3 h-3 text-zinc-600 cursor-help" />
                      <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-2 bg-zinc-800 rounded-lg text-xs text-zinc-300 w-48 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-10">
                        Wartość średnia. Oględziny kosztują 600-900 PLN w zależności od lokalizacji auta.
                      </div>
                    </div>
                  </div>
                  <span className="font-mono text-white">{formatPLN(FIXED_COSTS.inspection)}</span>
                </div>
                
                <div className="flex justify-between items-center py-2">
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-zinc-400">Sprawdzenie VIN</span>
                    <span className="text-[10px] text-zinc-600">(maks.)</span>
                  </div>
                  <span className="font-mono text-white">{formatPLN(FIXED_COSTS.vinCheck)}</span>
                </div>
              </div>
            </section>
          )}

          {/* Total Summary */}
          {canCalculate && (
            <section className="pt-6 border-t border-white/10 text-center">
              <p className="text-sm font-bold text-orange-500 uppercase tracking-[0.2em] mb-3">
                Cena na gotowo
              </p>
              <p 
                className="text-5xl md:text-7xl font-heading font-black tracking-tighter text-white text-shadow-glow"
                data-testid="total-price"
              >
                {formatPLN(totalPrice)}
              </p>
              
              {purchaseType === 'vat_invoice' && (
                <p className="text-xs text-orange-400 mt-2">
                  Faktura VAT 23% dla klienta
                </p>
              )}
              
              <div className="mt-6 p-4 bg-zinc-900/50 rounded-xl border border-white/5">
                <p className="text-xs text-zinc-500 leading-relaxed">
                  <span className="text-orange-500 font-semibold">Uwaga:</span> Cena "na gotowo" nie obejmuje: 
                  kosztu samej rejestracji w wydziale komunikacji, ubezpieczenia OC/AC oraz ewentualnego serwisu pojazdu.
                </p>
              </div>
            </section>
          )}

          {/* Empty State */}
          {!canCalculate && (
            <div className="text-center py-8">
              <Calculator className="w-12 h-12 text-zinc-700 mx-auto mb-4" />
              <p className="text-zinc-600 text-sm">
                Wprowadź cenę auta i wybierz typ silnika, aby obliczyć całkowity koszt importu
              </p>
            </div>
          )}
        </div>

        {/* Footer */}
        <footer className="mt-8 text-center space-y-2">
          <p className="text-xs text-zinc-600">
            Kurs EUR/PLN: kantorowy • Kurs NBP tylko do porównania
          </p>
          <a 
            href="/admin" 
            className="inline-block text-xs text-zinc-700 hover:text-orange-500 transition-colors"
            data-testid="admin-link"
          >
            Panel administracyjny
          </a>
        </footer>
      </main>
    </div>
  );
}
